import clsx from 'clsx';
import Link, { type LinkProps as NextLinkProps } from 'next/link';
import type React from 'react';
import { Spinner } from '../Spinner';
import styles from './Button.module.scss';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'cta'
  | 'inverted'
  | 'no-bg'
  | 'outline'
  | 'outline-inverted'
  | 'critical';

interface SharedProps {
  size?: ButtonSize;
  sizeLtMd?: ButtonSize;
  sizeMd?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  fullWidthLtMd?: boolean;
  noWrap?: boolean;
  testId?: string;
  hoverGhost?: boolean;
}

type ButtonProps = SharedProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined; // Disallow href for button
    loading?: boolean;
  };

type LinkProps = SharedProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  Pick<NextLinkProps, 'replace' | 'scroll' | 'prefetch' | 'locale'> & {
    href: string;
  };

export type ButtonComponentProps = ButtonProps | LinkProps;

export const Button = (props: ButtonComponentProps) => {
  const {
    size = 'md',
    sizeLtMd,
    sizeMd,
    variant = 'primary',
    fullWidth,
    fullWidthLtMd,
    noWrap,
    testId,
    className,
    children,
    hoverGhost,
    ...rest
  } = props;

  const classNames = clsx(styles.button, className, {
    [styles[`size--${size}`]]: size,
    [styles[`sizeMd--${sizeMd}`]]: sizeMd,
    [styles[`sizeLtMd--${sizeLtMd}`]]: sizeLtMd,
    [styles.variantPrimary]: variant === 'primary',
    [styles.variantSecondary]: variant === 'secondary',
    [styles.variantCta]: variant === 'cta',
    [styles.variantInverted]: variant === 'inverted',
    [styles.variantNoBg]: variant === 'no-bg',
    [styles.variantOutline]: variant === 'outline',
    [styles.variantOutlineInverted]: variant === 'outline-inverted',
    [styles.variantCritical]: variant === 'critical',
    [styles.fullWidth]: fullWidth,
    [styles.fullWidthLtMd]: fullWidthLtMd,
    [styles.noWrap]: noWrap,
    [styles.hoverGhost]: hoverGhost,
  });

  if ('href' in props && props.href) {
    const { href, replace, scroll, prefetch, locale, ...anchorProps } = rest as LinkProps;
    return (
      <Link
        href={href}
        replace={replace}
        scroll={scroll}
        prefetch={prefetch}
        locale={locale}
        className={classNames}
        data-testid={testId}
        {...anchorProps}
      >
        {children}
      </Link>
    );
  }

  const { loading, ...buttonProps } = rest as ButtonProps;
  return (
    <button
      type={buttonProps.type || 'button'}
      className={classNames}
      data-testid={testId}
      disabled={loading || buttonProps.disabled}
      {...buttonProps}
    >
      {children}
      {loading && <Spinner size="text" />}
    </button>
  );
};
